import React from "react";
import { useNavigate } from "react-router-dom";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../components/layout/view/ViewHeader";
import Text from "../../../../components/typography/Text";

import ContentGrid from "../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../components/feedback/EmptyState";
import Button from "../../../../components/buttons/Button";

import VideoThumbnailCard from "../../../../components/cards/thumbnails/VideoThumbnailCard";
import PopThumbnailCard from "../../../../components/cards/pops/PopThumbnailCard";
import AlbumThumbnailCard from "../../../../components/cards/album/AlbumCard";
import SharedTalentCard from "../../../talents/components/cards/SharedTalentCard";

import { useGlobalSearch } from "../hooks/useGlobalSearch";

import "./GlobalSearchView.css";

const SECTIONS = {
  videos: { title: "Videos", columns: 4 },
  pops: { title: "Pops", columns: 6 },
  albums: { title: "Albums", columns: 4 },
  streams: { title: "Streams", columns: 4 },
  talents: { title: "Talents", columns: 3 },
};

export default function GlobalSearchView({ type = "videos", search = "" }) {
  const navigate = useNavigate();

  const {
    results,
    total,
    hasMore,
    loading,
    loadingMore,
    error,
    hasSearch,
    validType,
    loadMore,
  } = useGlobalSearch(type, search);

  const section = SECTIONS[type];

  const handleSelectContent = (item) => {
    const routes = {
      videos: item.videoTag
        ? `/watch/${encodeURIComponent(item.videoTag)}`
        : null,

      pops: item.popTag ? `/pops/${encodeURIComponent(item.popTag)}` : null,

      albums: item.albumTag
        ? `/albums/${encodeURIComponent(item.albumTag)}`
        : null,

      streams: item.streamSessionId
        ? `/streams/${encodeURIComponent(item.streamSessionId)}`
        : null,

      talents: item.talentTag
        ? `/talents/${encodeURIComponent(item.talentTag)}`
        : null,
    };

    const path = routes[type];

    if (path) navigate(path);
  };

  const renderResult = (item) => {
    const onClick = () => handleSelectContent(item);

    switch (type) {
      case "videos":
        return (
          <VideoThumbnailCard video={item} onClick={onClick} showPlayButton />
        );

      case "pops":
        return <PopThumbnailCard pop={item} onClick={onClick} />;

      case "albums":
        return <AlbumThumbnailCard album={item} onClick={onClick} />;

      case "streams":
        return (
          <VideoThumbnailCard
            video={{
              ...item,
              videoId: item.streamSessionId,
              videoTag: String(item.streamSessionId),
            }}
            onClick={onClick}
            showPlayButton
          />
        );

      case "talents":
        return <SharedTalentCard talent={item} />;

      default:
        return null;
    }
  };

  if (!validType) {
    return (
      <ViewContainer>
        <EmptyState
          title="Invalid category"
          description="Please select a valid search category."
        />
      </ViewContainer>
    );
  }

  if (!hasSearch) {
    return (
      <ViewContainer>
        <EmptyState
          title="Search Katch"
          description="Select a category and enter a search term."
        />
      </ViewContainer>
    );
  }

  return (
    <ViewContainer>
      {loading && (
        <ContentGrid
          items={[]}
          loading
          columns={section.columns}
          tabletColumns={type === "pops" ? 4 : 3}
          mobileColumns={type === "talents" ? 1 : 2}
          gap={16}
          skeletonCount={12}
        />
      )}

      {!loading && error && results.length === 0 && (
        <EmptyState title="Search unavailable" description={error} />
      )}

      {!loading && !error && results.length === 0 && (
        <EmptyState
          title="No results found"
          description={`No matching ${section.title.toLowerCase()} found for "${search}".`}
        />
      )}

      {!loading && results.length > 0 && (
        <div className="global-search__section">
          <ViewHeader title={`${section.title} (${total})`} />

          <ContentGrid
            items={results}
            columns={section.columns}
            tabletColumns={type === "talents" ? 2 : type === "pops" ? 4 : 3}
            mobileColumns={type === "talents" ? 1 : 2}
            gap={16}
            showEmpty={false}
            renderItem={renderResult}
          />

          {error && (
            <div className="global-search__error" role="alert">
              <Text as="p" size="sm">
                {error}
              </Text>
            </div>
          )}

          {hasMore && (
            <div className="global-search__footer">
              <Button
                value={loadingMore ? "Loading..." : "Load More"}
                variant="primary-outlined"
                disabled={loadingMore}
                onClick={loadMore}
              />
            </div>
          )}
        </div>
      )}
    </ViewContainer>
  );
}
